import { useEffect, useRef, useState } from 'react';
import { DEMO } from '../demo/index.js';
import { demoBusSubscribe } from '../demo/tourBus.js';

export interface ParseProgressEvent {
  type: 'parse:progress';
  data: {
    fileId: string;
    status: 'pending' | 'parsing' | 'ok' | 'failed' | 'skipped';
    classification?: string | null;
    parsedPages?: number | null;
    ocrUsed?: boolean;
    parseError?: string | null;
    progressPct?: number;
  };
}
export type ProjectIndexStatus =
  | 'idle'
  | 'chunking'
  | 'embedding'
  | 'ready'
  | 'failed'
  | 'partial';
export interface IndexProgressEvent {
  type: 'index:progress';
  data: {
    stage: 'chunking' | 'embedding';
    fileId?: string;
    chunks?: number;
    batch?: number;
    totalSoFar?: number;
    embedded?: number;
    started?: boolean;
    done?: boolean;
    projectStatus?: ProjectIndexStatus;
    chunksTotal?: number;
    chunksEmbedded?: number;
  };
}
export type ProjectSummaryStatus = 'idle' | 'analyzing' | 'ready' | 'failed';
export interface AnalyzeProgressEvent {
  type: 'analyze:progress';
  data: {
    /** `stopped` = lo detuvo el usuario; no se guardó nada a medias. */
    stage: 'collect' | 'ai' | 'done' | 'failed' | 'stopped';
    filesUsed?: number;
    contextChars?: number;
    model?: string;
    detectedEntity?: string | null;
    detectedLicitNum?: string | null;
    summaryStatus?: ProjectSummaryStatus;
    error?: string | null;
    message?: string;
  };
}
export type QuoteStatus =
  | 'queued' | 'running' | 'paused' | 'done' | 'failed' | 'canceled';
export interface QuoteProgressEvent {
  type: 'quote:progress';
  data: {
    quoteId: string;
    stage:
      | 'queued' | 'extract' | 'pricing' | 'excel'
      | 'done' | 'failed' | 'paused' | 'stopped';
    status?: QuoteStatus;
    conceptsTotal?: number;
    conceptsProcessed?: number;
    conceptsPriced?: number;
    /** % global 0-100 calculado por el worker (pondera embeddings/Fase 1/Fase 2). */
    pct?: number;
    excelKey?: string;
    excelSizeBytes?: number;
    error?: string | null;
    message?: string;
    llmTokens?: { input: number; output: number };
    /**
     * Repetición del último estado (cada ~5 s) durante un paso largo. Señal de vida:
     * la pantalla dice con letras que sigue trabajando, nunca mueve el número.
     */
    latido?: boolean;
  };
}
export interface ApuIaProgressEvent {
  type: 'apu_ia:progress';
  data: {
    quoteId: string;
    /** `stopped` = lo detuvo el usuario; lo generado se conservó. */
    stage: 'start' | 'in_progress' | 'done' | 'failed' | 'stopped';
    conceptos_total: number;
    conceptos_procesados: number;
    /** Caption de la fase actual. */
    message?: string;
    /** % explícito 0..100 por fase (manda sobre procesados/total si viene). */
    pct?: number;
    /**
     * Repetición del último estado (cada ~5 s) durante un paso largo, aunque
     * ningún contador se mueva. Es señal de vida: la pantalla la usa para decir
     * con letras que sigue trabajando, NUNCA para mover el número.
     */
    latido?: boolean;
    cost_usd?: number;
    duration_ms?: number;
    error?: string | null;
  };
}
export interface QuoteOpusImportEvent {
  type: 'quote:opus-import';
  data: {
    quoteId: string;
    obraName: string;
    opusImportedAt: string;
  };
}
export interface HelloEvent {
  type: 'hello';
  data: { projectId: string; userId: string; tenantId: string };
}
export interface ErrorEvent {
  type: 'error';
  message: string;
}
export type ProjectEvent =
  | ParseProgressEvent
  | IndexProgressEvent
  | AnalyzeProgressEvent
  | QuoteProgressEvent
  | ApuIaProgressEvent
  | QuoteOpusImportEvent
  | HelloEvent
  | ErrorEvent;

export type ConnectionState = 'connecting' | 'open' | 'closed' | 'error';

interface UseProjectStreamOptions {
  projectId: string | undefined;
  onEvent: (ev: ProjectEvent) => void;
  /** Auto-reconnect en backoff exponencial. Default true. */
  reconnect?: boolean;
}

/**
 * Suscripción WebSocket a /api/v1/ws/projects/:id.
 * - Reusa la cookie HttpOnly (el navegador la manda automáticamente al WS
 *   mientras el origen coincida con el del API; en dev usamos el proxy de Vite).
 * - Reconnect con backoff (1s, 2s, 4s, max 10s).
 */
export function useProjectStream({
  projectId,
  onEvent,
  reconnect = true,
}: UseProjectStreamOptions): { state: ConnectionState } {
  const [state, setState] = useState<ConnectionState>('connecting');
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!projectId) return;
    if (DEMO) {
      // Modo demo: sin WebSocket — el bus del recorrido emite los MISMOS
      // eventos del worker (lectura, resumen, armado, equivalentes, APUs)
      // y las pantallas los animan igual que en producción.
      setState('open');
      const off = demoBusSubscribe((ev) => onEventRef.current(ev));
      return () => { off(); };
    }

    let socket: WebSocket | null = null;
    let retry = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    function buildUrl(): string {
      // Reusamos el proxy de Vite (`/api` con ws:true) — se conecta al mismo
      // origen que sirve la app y la cookie HttpOnly viaja automáticamente.
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${window.location.host}/api/v1/ws/projects/${projectId}`;
    }

    function connect() {
      if (cancelled) return;
      setState('connecting');
      socket = new WebSocket(buildUrl());

      socket.onopen = () => {
        retry = 0;
        setState('open');
      };
      socket.onmessage = (ev) => {
        try {
          const parsed = JSON.parse(ev.data) as ProjectEvent;
          onEventRef.current(parsed);
        } catch {
          // ignore non-JSON frames
        }
      };
      socket.onerror = () => {
        setState('error');
      };
      socket.onclose = () => {
        setState('closed');
        if (!reconnect || cancelled) return;
        const delay = Math.min(10_000, 1000 * 2 ** retry);
        retry++;
        reconnectTimer = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    };
  }, [projectId, reconnect]);

  return { state };
}
